import { apiRequest } from '../../shared/api/client';

/**
 * Serial inquiry — mirrors Invetrak.Api.Endpoints.SerialEndpoints, over tenant script 087.
 *
 *   GET /api/v1/serials[?search=&itemId=&onHandOnly=&pageSize=&pageNumber=]  serials:read
 *   GET /api/v1/serials/{id}                                                 serials:read
 *   GET /api/v1/serials/{id}/history[?pageSize=&pageNumber=]                 serials:read
 *
 * **Read-only, and there is nothing missing.** A serial is created by scanning it onto a receipt
 * or an adjustment, never by typing it into a form — legacy has no serial maintenance screen to
 * copy because it has no serial master at all. Editing a serial number after the fact would
 * rewrite what every ledger row already says; the correction is an adjustment.
 *
 * **Warehouse-scoped.** A warehouse is the 3PL client boundary, and these rows name the customer
 * who received the unit.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface SerialDto {
  serialId: string;
  serialNumber: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  lotId: string | null;
  lotNumber: string | null;
  expirationDateUtc: string | null;
  /** 1 while the unit is in the building, 0 once it has gone. */
  onHandQuantity: number;
  firstSeenAtUtc: string | null;
  lastMovementAtUtc: string | null;
  /** Null when the unit is not on hand. That is the answer, not a gap. */
  currentLocationId: string | null;
  currentLocationCode: string | null;
  currentLicensePlateNumber: string | null;
  /** Set once the unit has left. The most recent shipment, if it went out twice. */
  shippedAtUtc: string | null;
  orderNumber: string | null;
  customerName: string | null;
}

export interface SerialDetailDto {
  serialId: string;
  serialNumber: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  isSerialized: boolean;
  isLotControlled: boolean;
  lotId: string | null;
  lotNumber: string | null;
  expirationDateUtc: string | null;
  createdAtUtc: string;
  onHandQuantity: number;
  firstSeenAtUtc: string | null;
  lastMovementAtUtc: string | null;
  currentLocationId: string | null;
  currentLocationCode: string | null;
  currentLicensePlateNumber: string | null;
}

/**
 * Who received the unit.
 *
 * No `isExact` here, unlike the lot equivalent. A lot is split across orders, so a wave's ledger
 * rows cannot say who got how much and the lot trace has to report "may have received". A serial
 * is one unit: it goes to exactly one order, and since 086 both the direct path and the wave
 * split record it. Every row here is certain.
 */
export interface SerialCustomerDto {
  customerId: string;
  customerNumber: string;
  customerName: string;
  salesOrderId: string;
  orderNumber: string;
  customerPo: string | null;
  pickNumber: string | null;
  shippedAtUtc: string | null;
  fromWave: boolean;
}

/** Same shape as LotMovementDto on purpose — one component renders both. */
export interface SerialMovementDto {
  inventoryTransactionId: string;
  performedAtUtc: string;
  tranCode: string;
  tranCodeName: string;
  movementType: string | null;
  movementSign: number | null;
  quantityDelta: number;
  uomCode: string;
  warehouseCode: string;
  warehouseName: string;
  locationCode: string;
  balanceTypeCode: string | null;
  licensePlateNumber: string | null;
  /** Receipt, Shipment, Pick, Move or Adjustment. */
  documentType: string;
  documentNumber: string | null;
  counterparty: string | null;
  poNumber: string | null;
  orderNumber: string | null;
  packingSlipNumber: string | null;
  fromWave: boolean;
  performedBy: string | null;
}

export interface SerialPageDto {
  items: SerialDto[];
  totalCount: number;
}

export interface SerialReportDto {
  detail: SerialDetailDto;
  customers: SerialCustomerDto[];
}

export interface SerialMovementPageDto {
  movements: SerialMovementDto[];
  totalCount: number;
}

export async function listSerials(
  filter: { search?: string | null; itemId?: string | null; onHandOnly?: boolean } = {},
  page: { pageSize?: number; pageNumber?: number } = {},
  signal?: AbortSignal,
): Promise<SerialPageDto> {
  const q = new URLSearchParams();
  if (filter.search) q.set('search', filter.search);
  if (filter.itemId) q.set('itemId', filter.itemId);
  if (filter.onHandOnly) q.set('onHandOnly', 'true');
  if (page.pageSize) q.set('pageSize', String(page.pageSize));
  if (page.pageNumber) q.set('pageNumber', String(page.pageNumber));
  const query = q.toString();

  const { data } = await apiRequest<SerialPageDto>(
    `/v1/serials${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getSerial(serialId: string, signal?: AbortSignal): Promise<SerialReportDto> {
  const { data } = await apiRequest<SerialReportDto>(
    `/v1/serials/${encodeURIComponent(serialId)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function getSerialHistory(
  serialId: string,
  page: { pageSize?: number; pageNumber?: number } = {},
  signal?: AbortSignal,
): Promise<SerialMovementPageDto> {
  const q = new URLSearchParams();
  if (page.pageSize) q.set('pageSize', String(page.pageSize));
  if (page.pageNumber) q.set('pageNumber', String(page.pageNumber));
  const query = q.toString();

  const { data } = await apiRequest<SerialMovementPageDto>(
    `/v1/serials/${encodeURIComponent(serialId)}/history${query ? `?${query}` : ''}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}
